import {AuthContext, useApi} from "@buildwitt/shapeshift-identity";
import {API} from "@buildwitt/shapeshift-identity";
import {createContext, ReactElement, Dispatch, FC, useState, useEffect, useContext} from "react";
import {jwtDecode} from "jwt-decode";
import {ICohortEntity} from "@buildwitt/shapeshift-types";
import {OrganizationResponseDto} from "@buildwitt/identity-client";

const initialState: [PageBaseContextState, Dispatch<React.SetStateAction<PageBaseContextState>>] = [
	{sidebarEnabled: true, sidebarOpen: true, notificationDrawerOpen: false, notificationCount: 0, contentContainerRef: undefined, organizationName: ""},
	() => {}
];
const PageBaseContext = createContext(initialState);

type PageBaseContextState = {
	ownedCohort?: {title: string, id: number} | false,
	memberedCohort?: {title: string, id: number, comments?: boolean},
	bwAdmin?: boolean,
	sidebarEnabled: boolean,
	sidebarOpen: boolean,
	notificationDrawerOpen: boolean,
	notificationCount: number,
	contentContainerRef: any,
	organizationName: string;
}

interface IProps {
	children: ReactElement
}

const PageBaseProvider: FC<IProps> = ({children}) => {
	const localOwnedCohort = localStorage.getItem("ownedCohort");
	const localMemberedCohort = localStorage.getItem("memberedCohort");
	const localParsedCohortOwned = localOwnedCohort ? JSON.parse(localOwnedCohort) : false;
	const localParsedCohortMember = localMemberedCohort ? JSON.parse(localMemberedCohort) : false;
	const [state, setState] = useState<PageBaseContextState>({...initialState[0], ownedCohort: localParsedCohortOwned, memberedCohort: localParsedCohortMember});
	const [{data: ownedCohort, statusCode: ownedCohortStatusCode}] = useApi<false | {title: string, id: number}>(API.cohortIsOwner, localParsedCohortOwned);
	const [{data: memberedCohorts, statusCode: memberedCohortStatusCode}] = useApi<ICohortEntity[]>(API.memberedCohorts, localParsedCohortMember);
	const [{data: organization, statusCode: organizationStatusCode}] = useApi<OrganizationResponseDto>(localParsedCohortMember?.id ? `/api/cohort/${localParsedCohortMember.id}/organization` : null, undefined);
	const {jwt, authenticated} = useContext(AuthContext);


	useEffect(() => {
		if(organization && organizationStatusCode === 200){
			setState(prev => ({...prev, organizationName: organization.name}))
		}
	}, [organization, organizationStatusCode])

	useEffect(() => {
		if(ownedCohort && ownedCohortStatusCode === 200){
			setState(prev => ({...prev, ownedCohort}))
			localStorage.setItem("ownedCohort", JSON.stringify(ownedCohort))
		}
		else if(ownedCohortStatusCode === 200)
			localStorage.removeItem("ownedCohort");
	}, [ownedCohort, ownedCohortStatusCode])

	useEffect(() => {
		if(memberedCohorts?.[0] && memberedCohortStatusCode === 200){
			setState(prev => ({...prev, memberedCohort: memberedCohorts?.[0]}))
			localStorage.setItem("memberedCohort", JSON.stringify(memberedCohorts?.[0]))
		}
		else if(memberedCohortStatusCode === 200)
			localStorage.removeItem("memberedCohort");
	}, [memberedCohorts, memberedCohortStatusCode])

	useEffect(() => {
		if(jwt){
			const decoded: any = jwtDecode(jwt);
			const key = Object.keys(decoded.org_id_to_org_member_info)[0];
			const org = decoded.org_id_to_org_member_info[key];
			// Fallback to JWT value in case API call fails
			const fallbackOrganizationName = org?.org_name ?? "";

			const allRoles = new Set([
				...(org?.inherited_user_roles_plus_current_role ?? []),
				...(org?.additional_roles ?? []),
				org.user_role
			]);

			// Fetch latest organization name from API
			setState(prev => ({...prev, bwAdmin: allRoles.has("BuildWitt:Admin"), organizationName: organization?.name ?? fallbackOrganizationName}));
		}
	}, [jwt])

	useEffect(() => {
		if(!authenticated)
			setState(prev => ({...prev, bwAdmin: false}))
	}, [authenticated]);

	return (
		<PageBaseContext.Provider value={[state, setState]}>
			{children}
		</PageBaseContext.Provider>
	);
};

export {
	PageBaseContext,
	PageBaseProvider
};